import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserCheck,
  CalendarDays,
  DollarSign,
  ArrowUpRight,
  TrendingUp,
  CalendarClock,
  CreditCard,
} from "lucide-react";
import { fetchLeads, fetchClients, fetchAppointments, fetchInvoices, getGlobalActivity, money, formatDate } from "../../lib/api";
import { leadStatuses } from "../../mock";
import { useAuth } from "../../context/AuthContext";
import ActivityTimeline from "../common/ActivityTimeline";

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between">
        <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-gray-300" />
      </div>
      <div className="mt-4 font-serif text-3xl font-bold text-navy">{value}</div>
      <div className="text-sm text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-green-600 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [appts, setAppts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    fetchLeads().then(setLeads).catch(() => setLeads([]));
    fetchClients().then(setClients).catch(() => setClients([]));
    fetchAppointments().then(setAppts).catch(() => setAppts([]));
    fetchInvoices().then(setInvoices).catch(() => setInvoices([]));
    if (token) getGlobalActivity(token).then(setActivity).catch(() => setActivity([]));
  }, [token]);

  const payments = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const paid = invoices.filter((i) => i.status === "paid");
    const collectedMonth = paid
      .filter((i) => (i.paid_at || "").slice(0, 7) === ym)
      .reduce((s, i) => s + (i.total || 0), 0);
    const outstanding = invoices
      .filter((i) => i.status !== "paid" && i.status !== "void")
      .reduce((s, i) => s + (i.total || 0), 0);
    const recent = [...paid]
      .sort((a, b) => (a.paid_at || "") < (b.paid_at || "") ? 1 : -1)
      .slice(0, 5);
    return { collectedMonth, outstanding, recent };
  }, [invoices]);

  const revenue = useMemo(
    () => clients.reduce((sum, c) => sum + (c.value || 0), 0),
    [clients]
  );

  const pipeline = useMemo(() => {
    return leadStatuses.map((s) => ({
      status: s,
      count: leads.filter((l) => l.status === s).length,
    }));
  }, [leads]);
  const maxCount = Math.max(...pipeline.map((p) => p.count), 1);

  const recentLeads = leads.slice(0, 5);
  const upcoming = appts
    .filter((a) => a.status !== "Cancelled")
    .slice(0, 4);

  const todayISO = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const nowHM = (() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  })();
  const todays = appts
    .filter((a) => a.date === todayISO && a.status !== "Cancelled")
    .sort((a, b) => (a.time > b.time ? 1 : -1));
  const nextToday = todays.find((a) => a.time >= nowHM) || todays[0] || null;

  const statusColor = (s) =>
    ({
      New: "bg-blue-100 text-blue-700",
      Contacted: "bg-amber-100 text-amber-700",
      Qualified: "bg-purple-100 text-purple-700",
      Converted: "bg-green-100 text-green-700",
      Lost: "bg-gray-100 text-gray-500",
    }[s] || "bg-gray-100 text-gray-600");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-navy">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Overview of your leads, clients and activity.
        </p>
      </div>

      <button
        onClick={() => navigate("/crm/appointments")}
        className="w-full text-left bg-navy rounded-xl p-5 shadow-sm flex items-center gap-5 hover:bg-navy-light transition-colors"
        data-testid="todays-appointments-card"
      >
        <div className="h-12 w-12 rounded-lg bg-gold flex items-center justify-center flex-shrink-0">
          <CalendarClock className="h-6 w-6 text-navy" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-gold-light text-xs uppercase tracking-wider font-semibold">Today's Appointments</div>
          {todays.length === 0 ? (
            <div className="text-white font-medium mt-0.5">No sessions scheduled today</div>
          ) : (
            <div className="text-white font-medium mt-0.5">
              {todays.length} session{todays.length > 1 ? "s" : ""} today
              {nextToday && <span className="text-gold-light font-normal"> · next at {nextToday.time} with {nextToday.clientName}</span>}
            </div>
          )}
        </div>
        <ArrowUpRight className="h-5 w-5 text-gold-light flex-shrink-0" />
      </button>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Leads" value={leads.length} sub={`${pipeline[0].count} new`} accent="bg-blue-100 text-blue-600" />
        <StatCard icon={UserCheck} label="Active Clients" value={clients.filter((c) => c.stage === "Active").length} sub={`${clients.length} total`} accent="bg-green-100 text-green-600" />
        <StatCard icon={CalendarDays} label="Appointments" value={appts.length} sub={`${appts.filter((a) => a.status === "Confirmed").length} confirmed`} accent="bg-purple-100 text-purple-600" />
        <StatCard icon={DollarSign} label="Client Value" value={`$${revenue.toLocaleString()}`} sub="lifetime" accent="bg-gold/20 text-gold-dark" />
      </div>

      {/* Payments pulse */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="payments-widget">
        <div className="grid md:grid-cols-3">
          <button onClick={() => navigate("/crm/invoices")} className="text-left p-5 border-b md:border-b-0 md:border-r border-gray-100 hover:bg-cream/50 transition-colors">
            <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider font-semibold">
              <CreditCard className="h-4 w-4" /> Collected this month
            </div>
            <div className="mt-2 font-serif text-3xl font-bold text-green-600" data-testid="collected-month">{money(payments.collectedMonth)}</div>
          </button>
          <button onClick={() => navigate("/crm/invoices")} className="text-left p-5 border-b md:border-b-0 md:border-r border-gray-100 hover:bg-cream/50 transition-colors">
            <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider font-semibold">
              <DollarSign className="h-4 w-4" /> Outstanding
            </div>
            <div className="mt-2 font-serif text-3xl font-bold text-navy" data-testid="outstanding-total">{money(payments.outstanding)}</div>
          </button>
          <div className="p-5">
            <div className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-2">Recent payments</div>
            {payments.recent.length === 0 ? (
              <div className="text-sm text-gray-400">No payments yet.</div>
            ) : (
              <div className="space-y-1.5">
                {payments.recent.map((i) => (
                  <div key={i.id} className="flex items-center justify-between text-sm" data-testid={`recent-payment-${i.id}`}>
                    <span className="text-gray-600 truncate mr-2">{i.client_name} <span className="text-gray-300">·</span> <span className="text-gray-400">{i.paid_at ? formatDate(i.paid_at) : ""}</span></span>
                    <span className="font-medium text-green-600 whitespace-nowrap">{money(i.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pipeline */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-lg font-semibold text-navy">Lead Pipeline</h2>
            <TrendingUp className="h-5 w-5 text-gold" />
          </div>
          <div className="space-y-4">
            {pipeline.map((p) => (
              <div key={p.status}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-gray-600">{p.status}</span>
                  <span className="font-semibold text-navy">{p.count}</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-navy rounded-full transition-all"
                    style={{ width: `${(p.count / maxCount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming appointments */}
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="font-serif text-lg font-semibold text-navy mb-5">Upcoming</h2>
          <div className="space-y-4">
            {upcoming.map((a) => (
              <div key={a.id} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-cream flex flex-col items-center justify-center flex-shrink-0">
                  <CalendarDays className="h-4 w-4 text-gold-dark" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy truncate">{a.clientName}</div>
                  <div className="text-xs text-gray-500">{a.type}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{formatDate(a.date)} · {a.time}</div>
                </div>
              </div>
            ))}
            {upcoming.length === 0 && (
              <p className="text-sm text-gray-400">No upcoming appointments.</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-testid="dashboard-activity-widget">
        <h2 className="font-serif text-lg font-semibold text-navy mb-4">Recent Activity</h2>
        <div className="max-h-96 overflow-y-auto pr-1">
          <ActivityTimeline items={activity} showWho emptyText="No recent activity." />
        </div>
      </div>

      {/* Recent leads */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg font-semibold text-navy">Recent Leads</h2>
          <button onClick={() => navigate("/crm/leads")} className="text-sm text-gold-dark font-medium hover:underline">
            View all
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100">
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium hidden sm:table-cell">Service</th>
                <th className="pb-3 font-medium hidden md:table-cell">Source</th>
                <th className="pb-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentLeads.map((l) => (
                <tr key={l.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3">
                    <div className="font-medium text-navy">{l.name}</div>
                    <div className="text-xs text-gray-400">{l.email}</div>
                  </td>
                  <td className="py-3 text-gray-600 hidden sm:table-cell">{l.service || "—"}</td>
                  <td className="py-3 text-gray-600 hidden md:table-cell">{l.source}</td>
                  <td className="py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor(l.status)}`}>
                      {l.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
